/**
 * Knowledge Base Service - Handles API interactions for knowledge base management
 */

import axios from 'axios';
import { post, put, postStream, del } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_PERSISTENCE = `/api/v1/mms_persistence`;
const API_URL = `/api/v1/bluemormon/knowledgebase`;
const CHAT_LIST_API = `/api/v1/mms_persistence/conversation`;
const MESSAGE_LIST_API = `/api/v1/mms_persistence/message`;
const DOC_META_API = `/api/v1/mms_persistence/document_metadata`;
const API_INGEST_KB = `/api/v1/mms_ai_knowledgebase_service/knowledgebase/kb`;
const KB_EXECUTOR_SYNC_API = `/api/v1/mms_ai_executors/knowledgebase/execute/query/sync`;
const AGENT_EXECUTOR_SYNC_API = `/api/v1/mms_ai_executors/agent/execute/sync`;
const API_DATA_SOURCE = `/api/v1/mms_persistence/data_source`

/**
 * Ingest single File 
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const ingestFileToKb = async (knowledgeBaseId, fileNameList, otherOptions, pii_flag) => {
    let payload = {
        document_urls: fileNameList,
        payload: otherOptions,
        "async_execution": true,
        "mask_pii_data": pii_flag
    }
    try {
        const response = await post(`${API_URL}/${knowledgeBaseId}/ingest`, payload);
        return response;
    } catch (error) {
        console.error(`Error uploading file to knowledge base ${knowledgeBaseId}:`, error);
        throw error;
    }
};


/**
 * Ingest single File 
 * @param {string} knowledgeBaseCode - Knowledge base code
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const fetchKBStoredFiles = async (name, kbCode) => {
    try {
        const filter = { file_name: name, knowledge_base: kbCode }
        const response = await post(`/api/v1/mms_persistence/document_metadata/fetch`, { filter});
        return response;
    } catch (error) {
        console.error(`Error fetching the files}:`, error);
        throw error;
    }
};


/**
 * Ingest single File 
 * @param {string} knowledgeBaseCode - Knowledge base code
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const ingestFilesToKnowledgebase = async (knowledgeBaseCode, fileNames = {}, override=false) => {
    
    const payload = {
        kb_code: knowledgeBaseCode,
        document_urls: fileNames,
        json_payload: null,
        metadata: {
            source_name: "ui",
            ingested_by: "user"
        },
        override: override
    }
    try {
        const response = await post(`${API_INGEST_KB}/${knowledgeBaseCode}/ingest`, payload);
        return response;
    } catch (error) {
        console.error(`Error uploading file to knowledge base ${knowledgeBaseCode}:`, error);
        throw error;
    }
};


/**
 * Ingest single File 
 * @param {string} knowledgeBaseCode - Knowledge base code
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const deleteFilesToKnowledgebase = async (knowledgeBaseCode, fileNames = {}) => {
    
    const payload = {
        kb_code: knowledgeBaseCode,
        document_urls: fileNames,
        json_payload: null,
        metadata: {
            source_name: "ui",
            ingested_by: "user"
        },
        override: false
    }
    try {
        const response = await del(`${API_INGEST_KB}/${knowledgeBaseCode}`, payload);
        return response;
    } catch (error) {
        console.error(`Error uploading file to knowledge base ${knowledgeBaseCode}:`, error);
        throw error;
    }
};

/**
 * Delete the single File 
 * @param {string} tableName - Table Name
 * @param {number} recordId - File id
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const deleteFilesfromDataSource = async (tableName, recordId) => {
       try {
            const response = await del(`${API_PERSISTENCE}/${tableName}/${recordId}`);
            return response;
       } catch(error) {
        console.log("Error while deleting the files from datasource:", error)
       }
 
}



/**
 * delte data source 
 * @param {string} dataSourceCode - datasource  code
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const deleteDataSource = async (dataSourceCode) => {
    
    try {
        const payload = {code : dataSourceCode};
        const encodedFilter = encodeURIComponent(JSON.stringify(payload));
        const response = await del(`${API_DATA_SOURCE}?filter=${encodedFilter}`);
        return response;
    } catch (error) {
        console.error(`Error deleting data souce  ${dataSourceCode}:`, error);
        throw error;
    }
};

/**
 * Get all Chat Transactions with optional filtering
 * @param {Object} filters - Filter parameters
 * @param {Object} sort - Sort parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const getAllChatsByFilter = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${CHAT_LIST_API}/fetch?query=${query}`, { filter: filter, sort });
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Get all Chat Messages with optional filtering
 * @param {Object} filters - Filter parameters
 * @param {Object} sort - Sort parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const getAllMessagesByFilter = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${MESSAGE_LIST_API}/fetch?query=${query}`, { filter: filter, sort });
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Create ChatList
 * @param {Object} chatpayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const createChatForKb = async (chatpayload) => {
    try {
        const response = await post(`${CHAT_LIST_API}`, chatpayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Create MessageList
 * @param {Object} messagepayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const createMessageForChat = async (messagepayload) => {
    try {
        const response = await post(`${MESSAGE_LIST_API}`, messagepayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};

/**
 * Update ChatList
 * @param {Object} chatpayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const updateChatForKb = async (chatpayload) => {
    try {
        const response = await put(`${CHAT_LIST_API}`, chatpayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};

/**
 * Summarize a KB conversation into a short title and context using the non-streaming executor API.
 * This is called after a streamed answer completes.
 */
export const summarizeConversationForKb = async ({ knowledge_base_cd, session_id, user_id }) => {
    const body = {
        query: 'Please give a short succinct title and context for the conversation happened till now',
        session_id,
        user_id,
        knowledge_base_cd,
        system_prompt: 'You are rule engine in insurance. get everything from knowledgebase',
        payload: {},
        output_schema_example: {
            title: '',
            context: ''
        }
    };

    try {
        const response = await post(KB_EXECUTOR_SYNC_API, body);
        const content = response?.response_content || response?.response || response;
        const title = content?.title || '';
        const context = content?.context || '';
        return { title, context };
    } catch (error) {
        console.error('Error summarizing KB conversation:', error);
        throw error;
    }
};

/**
 * Execute the pipeline session agent for a given session/user and query.
 * Used by Meta Pilot when pipeline_summary_session_id and pipeline_summary_user_id are present.
 */
export const executePipelineSessionAgent = async ({ session_id, user_id, query }) => {
    const body = {
        query,
        session_id,
        user_id,
        agent_cd: 'DEFAULT_PIPELINE_SUMMARY_AGENT',
        payload: {}
    };

    try {
        const response = await post(AGENT_EXECUTOR_SYNC_API, body);
        const content = response?.response_content || response?.response || response;
        return content;
    } catch (error) {
        console.error('Error executing pipeline session agent:', error);
        throw error;
    }
};


/**
 * Search within a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Object>} Promise resolving to search results
 */
export const searchKnowledgeBase = async (knowledgeBaseId, query, context, conversation_id, type) => {
    try {
        const response = await post(`${API_URL}/${type}`, {
            query: query,
            context: context,
            knowledge_base: knowledgeBaseId,
            conversation_id: conversation_id
        });
        return response.response;
    } catch (error) {
        console.error(`Error searching knowledge base ${knowledgeBaseId}:`, error);
        throw error;
    }
};

/**
 * Search Doc meta data
 * @param {string} query - Search query
 * @param {Object} filter - Search options
 * @param {Object} sort - Search options
 * @returns {Promise<Object>} Promise resolving to search results
 */
export const getDocMetaDataWithFilters = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${DOC_META_API}/fetch?query=${query}`, {
            filter: filter,
            sort: sort
        });
        return response;
    } catch (error) {
        console.error(`Error searching knowledge base:`, error);
        throw error;
    }
};

export default {
    ingestFileToKb,
    getAllChatsByFilter,
    searchKnowledgeBase,
    getAllMessagesByFilter,
    getDocMetaDataWithFilters,
    executePipelineSessionAgent
};

/**
 * Stream agent query results from the backend and invoke onMessage for each parsed object.
 * Expects newline-delimited JSON or chunked JSON objects.
 * @param {{query:string, session_id?:string, user_id?:string}} data
 * @param {(obj:Object)=>void} onMessage
 */
export const streamAgentQuery = async (data = {}, streamUrl, onMessage) => {
    const res = await postStream(streamUrl, data);

    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Stream request failed (${res.status}): ${text}`);
    }

    if (!res.body) {
        const json = await res.json();
        if (typeof onMessage === 'function') onMessage(json);
        if (typeof onMessage === 'function') onMessage({ type: 'stream_done' });
        return json;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let done = false;

    let sseBuffer = '';

    while (!done) {
        const { value, done: rDone } = await reader.read();
        done = rDone;
        if (!value) continue;

        sseBuffer += decoder.decode(value, { stream: true });

        const events = sseBuffer.split(/\r?\n\r?\n/);
        sseBuffer = events.pop();

        for (const evt of events) {
            const lines = evt.split('\n');
            let eventType = 'message';
            let data = '';

            for (const line of lines) {
                if (line.startsWith('event:')) {
                    eventType = line.slice(6).trim();
                } else if (line.startsWith('data:')) {
                    data += line.slice(5).trim() + '\n';
                }
            }

            // Support agent, planner, and knowledge base streaming events
            if (eventType === 'agent_chunk' || eventType === 'planner_chunk' || eventType === 'kb_chunk') {
                const raw = data.trim();

                // Backend sometimes uses Python-style literals (None/True/False and single quotes).
                // Normalize a bit, then let tryParseJson handle the rest.
                const normalized = raw
                    .replace(/\bNone\b/g, 'null')
                    .replace(/\bTrue\b/g, 'true')
                    .replace(/\bFalse\b/g, 'false');

                const parsed = tryParseJson(normalized);

                if (!parsed) {
                    console.error('Failed to parse stream event data', eventType, raw);
                    continue;
                }

                if (typeof onMessage === 'function') onMessage(parsed);
            }
        }
    }

    if (typeof onMessage == 'function') onMessage({ type: 'stream_done' });
};


/**
 * Helper: Try to parse JSON, tolerant of single quotes for keys/values
 */
const tryParseJson = (text) => {
    try {
        return JSON.parse(text);
    } catch (e) {
        try {
            let s = text.replace(/(['\"])?([a-zA-Z0-9_]+)\1\s*:/g, '"$2":');
            s = s.replace(/:\s*'([^']*)'/g, ': "$1"');
            s = s.replace(/'([^']*)'/g, '"$1"');
            return JSON.parse(s);
        } catch (e2) {
            return null;
        }
    }
};

/**
 * Helper: Find matching closing brace for first '{' at pos start
 */
const findMatchingBrace = (str, start) => {
    if (start < 0) return -1;
    let depth = 0;
    for (let i = start; i < str.length; i++) {
        const ch = str[i];
        if (ch === '{') depth++;
        else if (ch === '}') {
            depth--;
            if (depth === 0) return i;
        }
    }
    return -1;
};

/**
 * Stream Knowledge Base query using the executor API.
 * Delegates to streamAgentQuery with the KB-specific endpoint and payload.
 */
export const streamKnowledgeBaseQuery = async (
    {
        query,
        session_id,
        user_id,
        knowledge_base_cd,
        system_prompt = 'You are helpful assistant',
        payload = {}
    },
    onMessage
) => {
    const body = {
        query,
        session_id,
        user_id,
        knowledge_base_cd,
        system_prompt,
        payload
    };

    return streamAgentQuery(
        body,
        '/api/v1/mms_ai_executors/knowledgebase/execute/query/sync/stream',
        onMessage
    );
};
